// eslint-disable max-lines
import { isArmedLandTransport } from "./transport";
import { UNIT_KINDS, type ProductionBuildingKind, type Unit, type UnitKind } from "./types";
import { applyUnitDefHoldover, type UnitDefHoldover } from "./unitDefHoldover";

export const MAX_HP = 100;

export type MovementType = "foot" | "vehicle" | "air";

export interface UnitDef {
  name: string;
  movement: number;
  movementType: MovementType;
  minRange: number;
  maxRange: number;
  buildAt: readonly ProductionBuildingKind[];
  damage: Partial<Record<UnitKind, number>>;
  cost: number;
  canCapture: boolean;
  canMoveAndShoot?: boolean;
}

export function isIndirectFire(kind: UnitKind): boolean {
  return UNIT_DEFS[kind].maxRange > 1;
}

const LAND_INDIRECT_SUPPORT_KINDS: ReadonlySet<UnitKind> = new Set(["artillery", "rockets"]);

export function isLandIndirectSupport(kind: UnitKind): boolean {
  return LAND_INDIRECT_SUPPORT_KINDS.has(kind);
}

export function isInAttackRange(kind: UnitKind, distance: number): boolean {
  const def = UNIT_DEFS[kind];
  return distance >= def.minRange && distance <= def.maxRange;
}

export function canMoveAndAttackSameTurn(kind: UnitKind): boolean {
  return UNIT_DEFS[kind].canMoveAndShoot ?? true;
}

export function effectiveAttackKind(unit: Pick<Unit, "kind" | "cargo">): UnitKind {
  if (isArmedLandTransport(unit)) return unit.cargo!.kind;
  return unit.kind;
}

export function isUnitInAttackRange(unit: Pick<Unit, "kind" | "cargo">, distance: number): boolean {
  return isInAttackRange(effectiveAttackKind(unit), distance);
}

export function effectiveAttackHp(unit: Pick<Unit, "kind" | "hp" | "cargo">): number {
  if (isArmedLandTransport(unit)) return unit.cargo!.hp;
  return unit.hp;
}

export function canUnitDamage({
  unit,
  defenderKind,
}: {
  unit: Pick<Unit, "kind" | "cargo">;
  defenderKind: UnitKind;
}): boolean {
  return (UNIT_DEFS[effectiveAttackKind(unit)].damage[defenderKind] ?? 0) > 0;
}

export function movementType(kind: UnitKind): MovementType {
  return UNIT_DEFS[kind].movementType;
}

export function isFootUnit(kind: UnitKind): boolean {
  return movementType(kind) === "foot";
}

export function isAirUnit(kind: UnitKind): boolean {
  return movementType(kind) === "air";
}

export const UNIT_DEF_HOLDOVER: UnitDefHoldover | null = {
  untilDate: "2026-08-30",
  defs: {
    artillery: {
      movement: 5,
    },
    rockets: {
      movement: 4,
    },
    missiles: {
      movement: 4,
    },
    lightTank: {
      movement: 6,
    },
  },
};

export const UNIT_DEFS_BASE: Record<UnitKind, UnitDef> = {
  rifleman: {
    name: "Rifleman",
    movement: 3,
    movementType: "foot",
    minRange: 1,
    maxRange: 1,
    buildAt: ["barracks", "base"],
    damage: {
      rifleman: 60,
      rpgInfantry: 55,
      sniper: 90,
      artillery: 15,
      rockets: 25,
      lightTank: 10,
      heavyTank: 5,
      antiAir: 10,
      missiles: 25,
      battleCopter: 10,
      landTransport: 15,
      transportCopter: 15,
    },
    cost: 100,
    canCapture: true,
  },
  rpgInfantry: {
    name: "RPG",
    movement: 2,
    movementType: "foot",
    minRange: 1,
    maxRange: 1,
    buildAt: ["barracks", "base"],
    damage: {
      rifleman: 65,
      rpgInfantry: 60,
      sniper: 90,
      artillery: 75,
      rockets: 85,
      lightTank: 60,
      heavyTank: 20,
      antiAir: 65,
      missiles: 85,
      battleCopter: 30,
      landTransport: 75,
      transportCopter: 50,
    },
    cost: 300,
    canCapture: true,
  },
  sniper: {
    name: "Sniper",
    movement: 3,
    movementType: "foot",
    minRange: 2,
    maxRange: 2,
    buildAt: ["barracks", "base"],
    damage: {
      rifleman: 120,
      rpgInfantry: 115,
      sniper: 130,
    },
    cost: 500,
    canCapture: true,
  },
  artillery: {
    name: "Artillery",
    movement: 6,
    movementType: "vehicle",
    minRange: 2,
    maxRange: 3,
    buildAt: ["factory", "base"],
    damage: {
      rifleman: 90,
      rpgInfantry: 85,
      sniper: 105,
      artillery: 85,
      rockets: 95,
      lightTank: 70,
      heavyTank: 45,
      antiAir: 75,
      missiles: 95,
      landTransport: 85,
    },
    cost: 600,
    canCapture: false,
    canMoveAndShoot: false,
  },
  rockets: {
    name: "Rocket Launcher",
    movement: 5,
    movementType: "vehicle",
    minRange: 3,
    maxRange: 5,
    buildAt: ["factory", "base"],
    damage: {
      rifleman: 110,
      rpgInfantry: 105,
      sniper: 120,
      artillery: 105,
      rockets: 105,
      lightTank: 85,
      heavyTank: 60,
      antiAir: 105,
      missiles: 105,
      landTransport: 105,
    },
    cost: 1300,
    canCapture: false,
    canMoveAndShoot: false,
  },
  lightTank: {
    name: "Light Tank",
    movement: 5,
    movementType: "vehicle",
    minRange: 1,
    maxRange: 1,
    buildAt: ["factory", "base"],
    damage: {
      rifleman: 80,
      rpgInfantry: 75,
      sniper: 95,
      artillery: 75,
      rockets: 85,
      lightTank: 60,
      heavyTank: 20,
      antiAir: 65,
      missiles: 85,
      battleCopter: 30,
      landTransport: 75,
      transportCopter: 50,
    },
    cost: 700,
    canCapture: false,
  },
  heavyTank: {
    name: "Heavy Tank",
    movement: 5,
    movementType: "vehicle",
    minRange: 1,
    maxRange: 1,
    buildAt: ["factory", "base"],
    damage: {
      rifleman: 110,
      rpgInfantry: 105,
      sniper: 120,
      artillery: 105,
      rockets: 105,
      lightTank: 85,
      heavyTank: 60,
      antiAir: 105,
      missiles: 105,
      landTransport: 105,
    },
    cost: 1500,
    canCapture: false,
  },
  antiAir: {
    name: "Anti-Air",
    movement: 6,
    movementType: "vehicle",
    minRange: 1,
    maxRange: 1,
    buildAt: ["factory", "base"],
    damage: {
      rifleman: 95,
      rpgInfantry: 90,
      sniper: 105,
      artillery: 30,
      rockets: 50,
      lightTank: 20,
      heavyTank: 10,
      antiAir: 50,
      missiles: 50,
      battleCopter: 120,
      fighterJet: 75,
      bomber: 85,
      landTransport: 30,
      transportCopter: 120,
    },
    cost: 700,
    canCapture: false,
  },
  missiles: {
    name: "SAM Battery",
    movement: 5,
    movementType: "vehicle",
    minRange: 2,
    maxRange: 5,
    buildAt: ["factory", "base"],
    damage: {
      battleCopter: 120,
      transportCopter: 120,
      fighterJet: 100,
      bomber: 100,
    },
    cost: 1100,
    canCapture: false,
    canMoveAndShoot: false,
  },
  landTransport: {
    name: "Land Transport",
    movement: 6,
    movementType: "vehicle",
    minRange: 1,
    maxRange: 1,
    buildAt: ["factory", "base"],
    damage: {},
    cost: 500,
    canCapture: false,
  },
  battleCopter: {
    name: "Battle Copter",
    movement: 6,
    movementType: "air",
    minRange: 1,
    maxRange: 1,
    buildAt: ["airport"],
    damage: {
      rifleman: 80,
      rpgInfantry: 75,
      sniper: 95,
      artillery: 75,
      rockets: 85,
      lightTank: 60,
      heavyTank: 25,
      antiAir: 25,
      missiles: 85,
      battleCopter: 60,
      landTransport: 75,
      transportCopter: 95,
    },
    cost: 900,
    canCapture: false,
  },
  fighterJet: {
    name: "Fighter",
    movement: 8,
    movementType: "air",
    minRange: 1,
    maxRange: 1,
    buildAt: ["airport"],
    damage: {
      battleCopter: 110,
      fighterJet: 60,
      bomber: 110,
      transportCopter: 120,
    },
    cost: 1600,
    canCapture: false,
  },
  bomber: {
    name: "Bomber",
    movement: 7,
    movementType: "air",
    minRange: 1,
    maxRange: 1,
    buildAt: ["airport"],
    damage: {
      rifleman: 120,
      rpgInfantry: 115,
      sniper: 130,
      artillery: 110,
      rockets: 110,
      lightTank: 110,
      heavyTank: 95,
      antiAir: 95,
      missiles: 110,
      landTransport: 110,
    },
    cost: 2000,
    canCapture: false,
  },
  transportCopter: {
    name: "Transport Copter",
    movement: 6,
    movementType: "air",
    minRange: 1,
    maxRange: 1,
    buildAt: ["airport"],
    damage: {},
    cost: 500,
    canCapture: false,
  },
};

export function unitDef(kind: UnitKind, atMs = Date.now()): UnitDef {
  return applyUnitDefHoldover({
    base: UNIT_DEFS_BASE,
    kind,
    holdover: UNIT_DEF_HOLDOVER,
    atMs,
  });
}

export const UNIT_DEFS = {} as Record<UnitKind, UnitDef>;
for (const kind of UNIT_KINDS) {
  Object.defineProperty(UNIT_DEFS, kind, {
    configurable: false,
    enumerable: true,
    get: () => unitDef(kind),
  });
}

export function damagePercentVsUnit({
  attackerKind,
  defenderKind,
}: {
  attackerKind: UnitKind;
  defenderKind: UnitKind;
}): number {
  if (attackerKind === "landTransport") return 0;
  return UNIT_DEFS[attackerKind].damage[defenderKind] ?? 0;
}

export function attackDamageMatchups(attackerKind: UnitKind): Array<{
  defenderKind: UnitKind;
  damagePercent: number;
}> {
  return UNIT_KINDS.map((defenderKind) => ({
    defenderKind,
    damagePercent: damagePercentVsUnit({ attackerKind, defenderKind }),
  }))
    .filter((matchup) => matchup.damagePercent > 0)
    .sort(
      (a, b) =>
        b.damagePercent - a.damagePercent ||
        UNIT_DEFS[a.defenderKind].name.localeCompare(UNIT_DEFS[b.defenderKind].name),
    );
}

export const BUILDABLE_BY_BUILDING: Record<ProductionBuildingKind, UnitKind[]> = {
  barracks: UNIT_KINDS.filter((k) => UNIT_DEFS[k].buildAt.includes("barracks")),
  factory: UNIT_KINDS.filter((k) => UNIT_DEFS[k].buildAt.includes("factory")),
  base: UNIT_KINDS.filter((k) => UNIT_DEFS[k].buildAt.includes("base")),
  airport: UNIT_KINDS.filter((k) => UNIT_DEFS[k].buildAt.includes("airport")),
};

export const UNIT_BUILD_HOTKEYS = {
  rifleman: "Q",
  rpgInfantry: "W",
  sniper: "E",
  lightTank: "A",
  heavyTank: "S",
  antiAir: "D",
  artillery: "F",
  rockets: "G",
  missiles: "H",
  landTransport: "J",
  battleCopter: "Z",
  fighterJet: "X",
  bomber: "C",
  transportCopter: "V",
} as const satisfies Partial<Record<UnitKind, string>>;

export function unitBuildHotkey(unitKind: UnitKind): string | null {
  return UNIT_BUILD_HOTKEYS[unitKind as keyof typeof UNIT_BUILD_HOTKEYS] ?? null;
}

export function productionBuildingBuildsFoot(buildingKind: ProductionBuildingKind): boolean {
  return BUILDABLE_BY_BUILDING[buildingKind].some((kind) => isFootUnit(kind));
}
